import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconBuildingBank, IconCash, IconX } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { datum, datumZaApi, denar } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { stevilo } from './vozilo';
import { PomocIkona } from '../pomoc/PomocIkona';

type Financiranje = Shema['FinanciranjePodatki'];

/**
 * Financiranje zaloge prek leasinga (novo v spletu): vozilo je z računom prodano leasing hiši, a ostane pri podjetju,
 * dokler ga leasing ne zaračuna nazaj. Prodano vozilo se lahko naknadno označi kot financirano; aktivno financiranje
 * se zaključi z odkupom (vozilo se vrne na zalogo, znesek postane nakupna cena – izračuna strežnik) ali prekliče.
 * Spodaj so pretekla financiranja.
 */
export function FinanciranjeVozila({
  voziloId,
  financiranja,
  lahkoFinancira,
}: {
  voziloId: number;
  financiranja: Financiranje[];
  lahkoFinancira: boolean;
}) {
  const odjemalec = useQueryClient();
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);
  const [odkup, setOdkup] = useState(false);
  const [preklic, setPreklic] = useState(false);
  if (financiranja.length === 0 && !lahkoFinancira) {
    return null;
  }
  const aktivno = financiranja.find((f) => !f.odkupDatum);
  const pretekla = financiranja.filter((f) => f.odkupDatum);
  const path = { id: voziloId };

  const izvedba = async (kdo: string, dejanje: () => Promise<unknown>, uspeh: string) => {
    setNapaka(null);
    setDela(kdo);
    try {
      await dejanje();
      await odjemalec.invalidateQueries({ queryKey: ['vozilo', voziloId] });
      notifications.show({ color: 'green', message: uspeh });
      setPreklic(false);
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  return (
    <Kartica naslov="Financiranje zaloge (leasing)" dejanja={<PomocIkona clanek="financiranje-zaloge" />}>
      <Stack gap="sm">
        {aktivno ? (
          <>
            <Text size="sm">
              Vozilo je z računom{' '}
              <Anchor component={Link} to={`/racuni/${aktivno.racunId}`} fw={600}>
                {aktivno.stRacuna}
              </Anchor>{' '}
              ({datum(aktivno.zacetek)}, {denar(aktivno.znesek)} €) prodano leasing hiši <b>{aktivno.leasing}</b> in je
              pri vas do odkupa. Lahko ga ponudite na predračunu; preden ga prodate kupcu, vnesite odkup od leasinga.
            </Text>
            <Group gap="xs">
              <Button size="xs" leftSection={<IconCash size={14} />} onClick={() => setOdkup(true)}>
                Vnesi odkup od leasinga
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconX size={14} />}
                onClick={() => setPreklic(true)}
              >
                Prekliči financiranje
              </Button>
            </Group>
          </>
        ) : lahkoFinancira ? (
          <>
            <Text size="sm" c="dimmed">
              Vozilo je prodano. Če je račun izdan leasing hiši za financiranje zaloge in je vozilo še pri vas, ga
              označite – ostane v evidenci financiranih vozil, na predračunih in javni ponudbi do odkupa.
            </Text>
            <Group>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconBuildingBank size={14} />}
                loading={dela === 'oznaci'}
                onClick={() =>
                  void izvedba(
                    'oznaci',
                    () => podatki(api.POST('/api/vozila/{id}/financiranje', { params: { path } })),
                    'Vozilo je označeno kot financirano pri leasingu.',
                  )
                }
              >
                Označi kot financirano pri leasingu
              </Button>
            </Group>
          </>
        ) : null}
        {pretekla.map((f) => (
          <Text key={f.id} size="xs" c="dimmed">
            Financirano pri {f.leasing} od {datum(f.zacetek)} (račun{' '}
            <Anchor component={Link} to={`/racuni/${f.racunId}`} size="xs">
              {f.stRacuna}
            </Anchor>
            ), odkupljeno {datum(f.odkupDatum)} za {denar(f.odkupZnesek)} €
            {f.odkupDokument ? ` (račun leasinga ${f.odkupDokument})` : ''}
            {f.odkupIzkazanDdv ? ', z izkazanim DDV' : ''}.
          </Text>
        ))}
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
      </Stack>
      <Modal opened={preklic} onClose={() => setPreklic(false)} title="Prekliči financiranje" radius="lg">
        <Text size="sm">
          Vozilo ne bo več med financiranimi; ostane prodano leasing hiši kot navadna prodaja. Uporabite, če ste ga
          označili po pomoti.
        </Text>
        <Group justify="flex-end" mt="md" gap="xs">
          <Button variant="subtle" onClick={() => setPreklic(false)}>
            Nazaj
          </Button>
          <Button
            color="red"
            loading={dela === 'preklic'}
            onClick={() =>
              void izvedba(
                'preklic',
                () => podatki(api.DELETE('/api/vozila/{id}/financiranje', { params: { path } })),
                'Financiranje je preklicano.',
              )
            }
          >
            Prekliči financiranje
          </Button>
        </Group>
      </Modal>
      <Modal opened={odkup} onClose={() => setOdkup(false)} title="Odkup od leasinga" radius="lg">
        {odkup && (
          <ObrazecOdkupa
            voziloId={voziloId}
            zapri={() => setOdkup(false)}
            shranjen={async () => {
              setOdkup(false);
              await odjemalec.invalidateQueries({ queryKey: ['vozilo', voziloId] });
              notifications.show({ color: 'green', message: 'Odkup je vnesen, vozilo je spet na zalogi.' });
            }}
          />
        )}
      </Modal>
    </Kartica>
  );
}

/** Račun leasing hiše za odkup: datum, znesek, številka in ali je DDV izkazan (napake po poljih s strežnika). */
function ObrazecOdkupa({
  voziloId,
  zapri,
  shranjen,
}: {
  voziloId: number;
  zapri: () => void;
  shranjen: () => Promise<void>;
}) {
  const [dan, setDan] = useState<string | null>(null);
  const [znesek, setZnesek] = useState<number | string>('');
  const [dokument, setDokument] = useState('');
  const [izkazan, setIzkazan] = useState(false);
  const [stopnja, setStopnja] = useState<number | string>(22);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  const shrani = async () => {
    setPolja({});
    setNapaka(null);
    setDela(true);
    try {
      await podatki(
        api.POST('/api/vozila/{id}/financiranje/odkup', {
          params: { path: { id: voziloId } },
          body: {
            datum: datumZaApi(dan),
            znesek: stevilo(znesek) ?? undefined,
            dokument: dokument.trim() || undefined,
            izkazanDdv: izkazan,
            ddvStopnja: izkazan ? (stevilo(stopnja) ?? undefined) : undefined,
          },
        }),
      );
      await shranjen();
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
      setDela(false);
    }
  };

  const zneski = { decimalScale: 2, decimalSeparator: ',', thousandSeparator: '.', rightSection: '€' } as const;
  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        Vnesite podatke z računa, s katerim vam leasing hiša zaračuna vozilo nazaj. Vozilo se vrne na zalogo, znesek
        odkupa postane nakupna cena (nabavna cena za razliko v ceni pri prodaji kupcu).
      </Text>
      <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
        <DatePickerInput
          label="Datum računa leasinga"
          value={dan}
          onChange={(d) => setDan(datumZaApi(d) ?? null)}
          valueFormat="D. M. YYYY"
          error={polja.datum}
          withAsterisk
        />
        <NumberInput
          label="Znesek odkupa"
          value={znesek}
          onChange={setZnesek}
          error={polja.znesek}
          withAsterisk
          {...zneski}
        />
      </SimpleGrid>
      <TextInput
        label="Številka računa leasinga"
        value={dokument}
        onChange={(e) => setDokument(e.currentTarget.value)}
        error={polja.dokument}
      />
      <Checkbox
        label="Leasing je na računu izkazal DDV"
        description="Potem prodaja kupcu ne more biti po posebni ureditvi in bo z izkazanim DDV."
        checked={izkazan}
        onChange={(e) => setIzkazan(e.currentTarget.checked)}
      />
      {izkazan && (
        <NumberInput
          label="Stopnja DDV"
          value={stopnja}
          onChange={setStopnja}
          error={polja.ddvStopnja}
          decimalScale={1}
          decimalSeparator=","
          min={0}
          max={100}
          rightSection="%"
          maw={200}
        />
      )}
      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="flex-end" gap="xs">
        <Button variant="subtle" onClick={zapri} disabled={dela}>
          Prekliči
        </Button>
        <Button onClick={() => void shrani()} loading={dela}>
          Vnesi odkup
        </Button>
      </Group>
    </Stack>
  );
}
